import type { CategoryProvenance } from '@/types';

/**
 * The provenance line, split so the host can emphasise the name it points at.
 * `name` is null wherever there is nothing to name — the line then reads as a complete
 * sentence on its own rather than trailing off into an empty emphasis.
 */
export interface CategoryProvenanceCopy {
  lead: string;
  name: string | null;
}

/**
 * One line under the category: "por que isso está em Alimentação?".
 *
 * The four sources are the precedence ladder the server already resolved — this only
 * words them. A rule that arrives without its label still explains itself, in less
 * detail, because the identity behind it can be demoted or merged between two reads and
 * saying nothing would be worse than saying less.
 */
export function categoryProvenanceCopy(
  provenance: CategoryProvenance | undefined,
): CategoryProvenanceCopy | null {
  if (!provenance) return null;

  switch (provenance.source) {
    case 'user':
      return { lead: 'Definida por você', name: null };
    case 'merchant_rule':
      return provenance.label
        ? { lead: 'Pela sua regra de ', name: provenance.label }
        : { lead: 'Pela sua regra deste estabelecimento', name: null };
    case 'descriptor_rule':
      return provenance.label
        ? { lead: 'Pela sua regra ', name: `“${provenance.label}”` }
        : { lead: 'Pela sua regra de descrição', name: null };
    default:
      return { lead: 'Categorizada automaticamente', name: null };
  }
}

/**
 * Whether the line can carry the user to the rule. Both halves are required: the ref is
 * what the "Regras" view is keyed by, and the id is what makes the destination the RULE
 * rather than a category page. Without them the line explains and stops there — which is
 * the honest outcome for a manual edit or an automatic categorization anyway.
 */
export function provenanceRuleTarget(
  provenance: CategoryProvenance | undefined,
): { ruleId: string; categoryRef: string } | null {
  if (!provenance?.ruleId || !provenance.categoryRef) return null;
  return { ruleId: provenance.ruleId, categoryRef: provenance.categoryRef };
}
